import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  byPresentationOrder,
  FREE_AUDIENCE_CAP,
  isValidJoinCode,
  normalizeJoinCode,
  type OptionFollowUp,
  type OwnAskedQuestion,
  type PublicAskedQuestion,
  type PublicQuizState,
  type Question,
  SCALE_PRESETS,
  type ServerEvent,
} from '@floorpoll/shared';
import { JoinCodeForm } from '../../components/JoinCodeForm.js';
import { ConsentBanner } from '../../analytics/ConsentBanner.js';
import { LegalLinks } from '../../components/LegalLinks.js';
import { SignupLink } from '../../components/SignupLink.js';
import { VendorMark } from '../../components/VendorMark.js';
import { ApiError, apiRequest } from '../../lib/api.js';
import {
  readRecap,
  reconcileRecap,
  rememberFollowUps,
  type RecapItem,
  type RecapPick,
} from '../../lib/followUpCache.js';
import { themeStyle } from '../../lib/theme.js';
import { useCountdown } from '../../lib/useCountdown.js';
import { useNoIndex } from '../../lib/useNoIndex.js';
import { useLiveQuiz } from '../../lib/useLiveQuiz.js';
import { FollowUpBlock, RecapList } from './TakerFollowUp.js';
import { TakerQanda } from './TakerQanda.js';

const DEVICE_TOKEN_KEY = 'floorpoll.deviceToken';

interface JoinPayload {
  state: PublicQuizState;
  deviceToken: string;
  /** questionId -> optionIds, so a refresh restores what this device answered. */
  answers: Record<string, string[]>;
  /** Replies for the options in `answers`. Options without one simply have none. */
  followUps: Record<string, OptionFollowUp[]>;
  /** #118 — approved questions for this run, in the moderator's order. */
  questions: PublicAskedQuestion[];
  /** #118 — this device's own, whatever their state. Carries rejection reasons. */
  myQuestions: OwnAskedQuestion[];
}

/**
 * Whether this device has handed in an answer to a question.
 *
 * Length, never truthiness: an answer is now a list, and `[]` is truthy — the
 * difference between a locked question and an untouched one.
 */
function hasAnswered(answers: Record<string, string[]>, questionId: string): boolean {
  return (answers[questionId] ?? []).length > 0;
}

/**
 * Pairs each reply with the text of the option that earned it.
 *
 * Guarded rather than trusted: this runs on a stranger's phone against a
 * payload from the network, and a throw here would take the whole voting view
 * down with it. A follow-up is the one thing on this screen worth losing on its
 * own.
 */
function picksFor(question: Question, followUps: OptionFollowUp[] | undefined): RecapPick[] {
  if (!Array.isArray(followUps)) return [];
  return followUps.map(({ optionId, followUp }) => ({
    optionText: question.options.find((o) => o.optionId === optionId)?.text ?? '',
    followUp,
  }));
}

/**
 * Recap rows derivable from a join payload — which means the **current screen
 * only**. A taker's payload carries one screen, so the question and option text
 * for earlier screens is not in it; those rows come from the cache, which
 * rememberFollowUps merges into rather than replaces. Cache and device token
 * live in the same storage, so a phone that has answers always has the text.
 */
function recapItemsFrom(payload: JoinPayload): RecapItem[] {
  const items: RecapItem[] = [];
  for (const question of payload.state.screen?.questions ?? []) {
    const picks = picksFor(question, payload.followUps[question.questionId]);
    if (picks.length === 0) continue;
    items.push({ questionId: question.questionId, questionText: question.text, picks });
  }
  return items;
}

/**
 * A question with the moderator's note taken off (#118).
 *
 * Its own function so the omission is a name rather than a destructure buried
 * in a reducer, where the next person merging a field would not see it.
 */
function omitRejectionReason(question: OwnAskedQuestion): OwnAskedQuestion {
  const { rejectionReason: _cleared, ...rest } = question;
  return rest;
}

/** Stories #23 and #24 — the audience's phone. Anonymous, one tap per question. */
export function Join() {
  useNoIndex();
  const { code: rawCode } = useParams();
  const navigate = useNavigate();
  const [payload, setPayload] = useState<JoinPayload | null>(null);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  // What a multi-select question has raised but not yet handed in. Cleared by
  // the submission that consumes it, and never consulted for a single-choice
  // question, where the tap itself is the submission.
  const [drafts, setDrafts] = useState<Record<string, string[]>>({});
  // Starts empty rather than seeded from readRecap: painting the cache before
  // the server has answered would show follow-ups for votes that may no
  // longer exist — most visibly right after a presenter resets results, which
  // clears every device's vote lock. The server is the authority while the
  // poll is live; the cache is only the fallback once resync has failed.
  const [recap, setRecap] = useState<RecapItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(rawCode));

  const code = rawCode ? normalizeJoinCode(rawCode) : '';

  // The questions this device asked. Held in a ref because the socket callback
  // below is built once and must not close over stale state.
  const myQuestionIdsRef = useRef<Set<string>>(new Set());
  myQuestionIdsRef.current = new Set(
    (payload?.myQuestions ?? []).map((question) => question.askedQuestionId),
  );

  const resync = useCallback(async () => {
    if (!code) return;
    try {
      const stored = localStorage.getItem(DEVICE_TOKEN_KEY);
      const query = stored ? `?deviceToken=${encodeURIComponent(stored)}` : '';
      const result = await apiRequest<JoinPayload>(`/public/join/${code}${query}`, {
        anonymous: true,
      });
      localStorage.setItem(DEVICE_TOKEN_KEY, result.deviceToken);
      setPayload(result);
      setAnswers(result.answers);
      // The server is the source of truth while the poll is live. Reconciling
      // here is what drops entries after a results reset, which clears this
      // device's vote lock and lets it answer again — and it is also what
      // clears a cache entry sitting under a recycled join code.
      rememberFollowUps(code, result.state.title, recapItemsFrom(result));
      setRecap(reconcileRecap(code, Object.keys(result.answers)));
      setError(null);
    } catch (caught) {
      // The join code stopped resolving — most often because the
      // presentation ended. The server can no longer reconcile anything, so
      // fall back to whatever this phone collected before that happened.
      setRecap(readRecap(code));
      setError(
        caught instanceof ApiError ? caught.message : 'No presentation is running with that code.',
      );
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    if (code && isValidJoinCode(code)) void resync();
    else if (code) {
      setError('No presentation is running with that code.');
      setLoading(false);
    }
  }, [code, resync]);

  const onEvent = useCallback(
    (event: ServerEvent) => {
      // A fresh presentation may follow a results reset, which clears this
      // device's vote lock server-side. Re-reading answers here stops the
      // phone showing "vote recorded" for a vote that no longer exists.
      if (event.type === 'state') {
        void resync();
        return;
      }

      // #118 — how a rejection reaches the person who asked.
      //
      // The push carries an id and nothing else, so it is addressed to one
      // phone without being addressable: this device re-reads only when the
      // id is one of its own, and every other phone in the room drops it
      // without a request. The reason lives on that re-read, because it is
      // never on the wire to anyone else.
      if (
        event.type === 'questionModerated' &&
        event.status === 'rejected' &&
        myQuestionIdsRef.current.has(event.askedQuestionId)
      ) {
        void resync();
        return;
      }

      setPayload((current) => {
        if (!current) return current;
        switch (event.type) {
          case 'screen':
            return {
              ...current,
              state: {
                ...current.state,
                currentScreenIndex: event.currentScreenIndex,
                screen: event.screen,
                // Advancing reopens voting server-side; copied here because
                // this reducer merges a fixed field list off the event, and a
                // stale close would leave the new screen's buttons locked.
                resultsVisible: event.resultsVisible,
                votingClosed: event.votingClosed,
                // The new screen's clock (#77), for the same reason: a
                // deadline left unmerged would run the previous screen's
                // countdown against this screen's question.
                screenDeadlineAt: event.screenDeadlineAt,
                serverTime: event.serverTime,
              },
            };
          case 'controls':
            // A taker's copy of this event carries no numbers — only whether
            // the question still takes answers.
            if (event.currentScreenIndex !== current.state.currentScreenIndex) return current;
            return {
              ...current,
              state: {
                ...current.state,
                resultsVisible: event.resultsVisible,
                votingClosed: event.votingClosed,
                // #118 — merged for the same reason the two above are: this
                // reducer copies a fixed field list off the event, so a
                // presenter closing Q&A would otherwise leave the ask box up.
                qandaOpen: event.qandaOpen,
              },
            };
          case 'ended':
            return { ...current, state: { ...current.state, status: 'draft', screen: null } };

          // #118 — the Q&A pushes a phone is allowed. Handled explicitly
          // because this reducer's default is to ignore, so an unhandled Q&A
          // event would be a feed that silently never updates.
          //
          // 'questionAsked', a rejection, and 'questionDeleted' are absent on
          // purpose: the server sends none of them to a taker. That is the
          // moderation gate — a phone never sees text nobody has approved.
          case 'questionModerated':
            // A rejection carries no content and is handled above — for this
            // device by a re-read, and for every other phone by being dropped.
            if (event.status === 'rejected' || !event.question) return current;
            return {
              ...current,
              questions: [
                ...current.questions.filter((q) => q.askedQuestionId !== event.askedQuestionId),
                event.question,
              ].sort(byPresentationOrder),
              // ...and if this phone asked it, its own copy moves too, so the
              // "waiting for a moderator" line becomes "in the queue".
              //
              // The reason is dropped rather than merged over. The console
              // offers "Approve after all" on the rejected column, and the
              // server clears the field on approve — a merge that kept it
              // would print "In the queue" with "off topic" underneath, on a
              // stranger's phone. Exactly the fixed-field-merge trap every
              // other reducer here warns about.
              myQuestions: current.myQuestions.map((q) =>
                q.askedQuestionId === event.askedQuestionId
                  ? { ...omitRejectionReason(q), status: 'approved' as const }
                  : q,
              ),
            };

          case 'questionLiked':
            return {
              ...current,
              questions: current.questions.map((q) =>
                q.askedQuestionId === event.askedQuestionId ? { ...q, likes: event.likes } : q,
              ),
            };

          case 'questionAnswered':
            return {
              ...current,
              questions: current.questions.map((q) =>
                q.askedQuestionId === event.askedQuestionId
                  ? { ...q, answer: event.answer, answeredAt: event.answeredAt }
                  : q,
              ),
              myQuestions: current.myQuestions.map((q) =>
                q.askedQuestionId === event.askedQuestionId
                  ? { ...q, answer: event.answer, answeredAt: event.answeredAt }
                  : q,
              ),
            };

          case 'questionPresented':
            return {
              ...current,
              state: { ...current.state, presentedAskedQuestionId: event.askedQuestionId },
            };

          default:
            // A taker never receives 'vote' — the server does not send it.
            return current;
        }
      });
    },
    [resync],
  );

  // Subscribes by join code: a taker is never told the quizId, because that id
  // is what unlocks the big-screen results feed.
  useLiveQuiz({
    target: code && isValidJoinCode(code) ? { code } : null,
    view: 'taker',
    onEvent,
    onResync: resync,
  });

  // Beside the other hooks and above `vote`, because hooks cannot sit after the
  // early returns below and this value gates the submission. Both arguments are
  // optional, so a payload that has not landed yet is simply no clock. Ticking
  // here rather than inside the question list is what re-renders the buttons at
  // zero.
  const countdown = useCountdown(payload?.state.screenDeadlineAt, payload?.state.serverTime);
  const expired = countdown?.expired === true;

  /** Hands in this question's answer — one option, or several at once. */
  async function vote(questionId: string, optionIds: string[]) {
    // The closed check is belt-and-braces beside the disabled buttons: a close
    // landing between a tap and its handler would otherwise send a request the
    // server is only going to refuse.
    if (!payload || payload.state.votingClosed || expired) return;
    if (hasAnswered(answers, questionId) || optionIds.length === 0) return;
    setPending(questionId);
    setError(null);

    try {
      const result = await apiRequest<{ followUps: OptionFollowUp[] }>(
        `/public/join/${code}/vote`,
        {
          method: 'POST',
          anonymous: true,
          body: { deviceToken: payload.deviceToken, questionId, optionIds },
        },
      );
      setAnswers((current) => ({ ...current, [questionId]: optionIds }));
      // The draft has become an answer; keeping it would leave a stale set
      // behind if a results reset ever unlocks this question again.
      setDrafts((current) => {
        const { [questionId]: _consumed, ...rest } = current;
        return rest;
      });

      // Bound to a local so TypeScript narrows it for the closures below.
      const followUps = result.followUps ?? [];
      if (followUps.length > 0) {
        const question = payload.state.screen?.questions.find((q) => q.questionId === questionId);
        setPayload((current) =>
          current
            ? { ...current, followUps: { ...current.followUps, [questionId]: followUps } }
            : current,
        );
        if (question) {
          const entry: RecapItem = {
            questionId,
            questionText: question.text,
            picks: picksFor(question, followUps),
          };
          rememberFollowUps(code, payload.state.title, [entry]);
          setRecap((current) => [...current.filter((c) => c.questionId !== questionId), entry]);
        }
      }
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'conflict') {
        // Already answered, or the room moved on — reconcile with the server.
        await resync();
      }
      setError(caught instanceof ApiError ? caught.message : 'Could not record your vote.');
    } finally {
      setPending(null);
    }
  }

  /**
   * #118 — this device just asked something. Added to its own list only: the
   * question is pending, so it is not in the room's feed and must not appear
   * there until a moderator says so.
   */
  function onAsked(question: OwnAskedQuestion) {
    setPayload((current) =>
      current ? { ...current, myQuestions: [...current.myQuestions, question] } : current,
    );
  }

  /**
   * The count this device's own tap produced.
   *
   * Applied locally as well as arriving as a broadcast, because the two race:
   * the push can land before the response does, and taking the larger of the
   * two is what stops the number flickering backwards.
   */
  function onLiked(askedQuestionId: string, likes: number) {
    setPayload((current) =>
      current
        ? {
            ...current,
            questions: current.questions.map((q) =>
              q.askedQuestionId === askedQuestionId ? { ...q, likes: Math.max(q.likes, likes) } : q,
            ),
          }
        : current,
    );
  }

  /** Raises or lowers one paddle on a multi-select question. */
  function toggleDraft(questionId: string, optionId: string) {
    setDrafts((current) => {
      const raised = current[questionId] ?? [];
      return {
        ...current,
        [questionId]: raised.includes(optionId)
          ? raised.filter((id) => id !== optionId)
          : [...raised, optionId],
      };
    });
  }

  // No code in the URL: offer the code-entry form.
  if (!rawCode) {
    return (
      <div className="taker taker-plain">
        <div className="card">
          <h1>Join a poll</h1>
          <p className="muted">Enter the code shown on the screen.</p>
          <JoinCodeForm />
        </div>
        <LegalLinks />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="taker taker-plain">
        <p className="muted">Joining…</p>
      </div>
    );
  }

  if (!payload) {
    return (
      <div className="taker taker-plain">
        <div className="card">
          <h1>No active presentation</h1>
          <p className="muted">{error ?? 'Check the code on the screen and try again.'}</p>
          <button type="button" className="secondary block" onClick={() => navigate('/j')}>
            Enter a different code
          </button>
        </div>
        {/* Reached on a fresh page load once the join code stops resolving —
            exactly the case the recap cache exists for. `recap` was populated
            from readRecap in resync's catch. */}
        <RecapList items={recap} />
        <LegalLinks />
      </div>
    );
  }

  const { state } = payload;
  const style = themeStyle(state.theme);
  // Stories #49 and #77 — two reasons, one closed state. The server refuses a
  // late vote either way; this is what stops a taker tapping into a refusal.
  // Absent on a payload from before the flags existed, which reads as open.
  const votingClosed = state.votingClosed === true || expired;

  if (state.status !== 'live' || !state.screen) {
    return (
      <div className="taker" style={style}>
        <div className="taker-message">
          <h1>The presentation has ended</h1>
          <p>Thanks for taking part.</p>
          {/* The audience loop's one exposed step. The mark is deliberately
              not a link because a taker mid-vote must never be led away from
              the poll; this screen is after the vote, so the same voter may
              be offered the door. Gated on the server's own answer, like the
              mark: a workspace that paid for a screen without us on it gets a
              phone without us on it. SignupLink derives `via=join` from the
              path, which is what makes this channel countable at all. */}
          {state.vendorMark ? (
            <p className="taker-door">
              {/* `slot` is required of every one of these, but no click here
                  is ever sent: this is a phone that has just been voting, and
                  trackEvent refuses every audience surface. The `via=join`
                  attribution the link carries is first-party and unaffected. */}
              <SignupLink slot="body">Run your own poll</SignupLink> — free for rooms up to{' '}
              {FREE_AUDIENCE_CAP} people.
            </p>
          ) : null}
        </div>
        <RecapList items={recap} />
        {state.vendorMark ? <VendorMark /> : null}
        <LegalLinks />
        {/* The audience's only consent prompt, and it waits for this screen.
            App suppresses the banner on every `/j` route precisely so this one
            can raise it: a phone that has just scanned a code is mid-task, and
            a modal between the scan and the first question is the product's
            core promise broken to ask a question that keeps until the end. For
            the length of the poll that phone sends cookieless pings and stores
            nothing, which is what an unanswered banner leaves running. */}
        <ConsentBanner />
      </div>
    );
  }

  return (
    <div className="taker" style={style}>
      {state.theme.logoUrl ? <img src={state.theme.logoUrl} alt="" className="taker-logo" /> : null}

      {/* Story #77. One clock per screen, above the questions it governs.
          `aria-live` is off deliberately: a value that changes every second
          would make a screen reader unusable, and the expiry message below
          each question is the announcement that matters. */}
      {countdown ? (
        <p className={`taker-countdown${countdown.expired ? ' expired' : ''}`}>
          <span className="taker-countdown-value">{countdown.label}</span>
          <span className="taker-countdown-unit">
            {countdown.expired ? "time's up" : 'seconds left'}
          </span>
        </p>
      ) : null}

      {state.screen.questions.map((question) => {
        const questionId = question.questionId;
        const answered = hasAnswered(answers, questionId);
        const multi = question.multiSelect === true;
        const busy = pending === questionId;
        // #50 — a rating votes exactly as a single-choice question does; what
        // changes is that its points are a row a thumb can span rather than a
        // column of paddles, so eleven of them still fit on a phone.
        const scale = question.scale ? SCALE_PRESETS[question.scale] : undefined;

        // Once handed in, the answer is what the server recorded. Before that,
        // a multi-select question shows what is raised on this phone, and a
        // single-choice one shows nothing — its tap is its submission.
        const raised = answered
          ? (answers[questionId] ?? [])
          : multi
            ? (drafts[questionId] ?? [])
            : [];
        const picks = picksFor(question, payload.followUps[questionId]);

        return (
          <section className="taker-question" key={questionId}>
            <h2>{question.text}</h2>
            {/* #75 — above the options, because it is what the question is
              about. Empty alt: `option.text` and the heading carry the meaning,
              and a filename read aloud carries none. */}
            {question.imageUrl ? (
              <img className="taker-question-image" src={question.imageUrl} alt="" />
            ) : null}
            {multi && !answered && !votingClosed ? (
              <p className="taker-rule">Pick as many as you like, then hand them in.</p>
            ) : null}

            {answered ? (
              <p className="answer-recorded" role="status">
                {raised.length > 1 ? 'Votes recorded' : 'Vote recorded'}
              </p>
            ) : votingClosed ? (
              <p className="voting-closed" role="status">
                {/* The reason, not just the state: "time is up" is something
                    the room watched happen, and reading "the presenter closed
                    it" instead would be wrong about who did what. */}
                {expired ? 'Time is up on this question.' : 'Voting has closed on this question.'}
              </p>
            ) : null}
            {answered
              ? picks.map((pick, index) => (
                  <div className="followup-pick" key={`${questionId}-followup-${index}`}>
                    {/* Named only when there is more than one reply to tell
                        apart; with a single pick the choice is already stamped
                        on the paddle below. */}
                    {picks.length > 1 ? <p className="followup-for">{pick.optionText}</p> : null}
                    <FollowUpBlock followUp={pick.followUp} />
                  </div>
                ))
              : null}

            <div
              className={`taker-options${scale ? ' taker-scale' : ''}${
                answered || votingClosed ? ' locked' : ''
              }`}
            >
              {question.options.map((option) => {
                const chosen = raised.includes(option.optionId);
                return (
                  <button
                    key={option.optionId}
                    type="button"
                    className={`taker-option${chosen ? ' chosen' : ''}`}
                    // A paddle labels itself; a scale point is a bare number,
                    // which read aloud on its own says nothing about how high
                    // it is. The anchors below are decoration for the same
                    // reason — a screen reader gets the range from here.
                    aria-label={scale ? `${option.text} out of ${scale.max}` : undefined}
                    // Locked once handed in (§5). A single-choice question
                    // locks on the tap itself; a multi-select one stays live
                    // until its submission lands, so picks can be changed.
                    // Also locked once the presenter closes voting (#49) —
                    // cosmetic only, the refusal is server-side.
                    disabled={answered || busy || votingClosed}
                    aria-pressed={chosen}
                    onClick={() =>
                      multi
                        ? toggleDraft(questionId, option.optionId)
                        : void vote(questionId, [option.optionId])
                    }
                  >
                    {/* Above the text, not instead of it: `option.text` stays
                      required, so the paddle is still labelled when the image
                      is slow, broken, or being read aloud. */}
                    {option.imageUrl ? (
                      <img className="taker-option-image" src={option.imageUrl} alt="" />
                    ) : null}
                    {option.text}
                    {chosen ? (
                      <span
                        className={answered ? 'paddle-check' : 'paddle-check paddle-raised'}
                        aria-hidden="true"
                      >
                        {answered ? '✓' : ''}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>

            {/* What the ends mean. Hidden from screen readers because each
                point already says "out of N", and reading two loose labels
                after eleven buttons would not attach them to anything. */}
            {scale ? (
              <p className="taker-scale-anchors" aria-hidden="true">
                <span>{scale.minLabel}</span>
                <span>{scale.maxLabel}</span>
              </p>
            ) : null}

            {multi && !answered ? (
              <button
                type="button"
                className="taker-submit block"
                disabled={raised.length === 0 || busy || votingClosed}
                onClick={() => void vote(questionId, raised)}
              >
                {raised.length === 0
                  ? 'Pick at least one'
                  : `Submit ${raised.length} ${raised.length === 1 ? 'pick' : 'picks'}`}
              </button>
            ) : null}
          </section>
        );
      })}

      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}

      <RecapList items={recap} />

      {/* #118 — under the ballot, because voting is what the room came for and
          Q&A is what it does between questions. Rendered only while the
          presenter has Q&A open, so a poll that never uses the feature looks
          exactly as it did before this shipped. */}
      {state.qandaOpen ? (
        <TakerQanda
          code={code}
          deviceToken={payload.deviceToken}
          questions={payload.questions}
          myQuestions={payload.myQuestions}
          presentedAskedQuestionId={state.presentedAskedQuestionId}
          onAsked={onAsked}
          onLiked={onLiked}
        />
      ) : null}

      <p className="taker-footer">Results are on the big screen.</p>
      {/* #91 — the vendor mark, under the poll and above the legal links,
          which are not removable at any price. Rendered on the server's word:
          `vendorMark` comes off the entitlement stamped when this run started,
          so no query parameter and no self-declared view can take it off.

          Absent from the two states above it — a phone that arrived with no
          code, and one whose code has stopped resolving. Neither is a poll,
          and with no payload there is no plan to answer from. */}
      {state.vendorMark ? <VendorMark /> : null}
      <LegalLinks />
    </div>
  );
}
